import * as React from 'react';
import * as _ from 'lodash';
import {
  API,
  CertificateReportStatus,
  CertificateStatus,
  getUuidV4,
  Logging,
  UserInfo,
  PermissionGroup,
  UserContext,
  getCreatePermission,
} from '@vcd-ecm-ui/Common';
import {
  GenericGrid,
  ActionMenu,
  ColumnLink,
  FilterModelApi,
  GridDataSummaryCollection,
  SortModelApi,
  Action,
  ActionDivider,
  mergeDefaultAndCustomColumns,
  ExpanderIcons,
} from '@vcd-ecm-ui/GenericGrid';
import { VtxButton, VtxColumnProps, VtxIcon } from '@vertexinc/vtx-ui-react-component-library';
import CertificateReportsProps from './CertificateReportsProps.interface';
import GridRowData, { GridDataNativeType } from './GridRowData';
import getDefaultColumns from './defaultColumns';
import '../common-reports.scss';
import './certificate-reports.scss';

const CertificateReports = ({
  gatewayEndPoint,
}: CertificateReportsProps): JSX.Element => {
  // Get the User Context
  const userContext: UserInfo = React.useContext(UserContext);
  const permissionReportsCreate: boolean = getCreatePermission(userContext, PermissionGroup.Reports);

  // Row is Expanded
  const expandedRowRender = (row: GridRowData): JSX.Element => {
    Logging.Debug('expandedRowRender', row);

    const startDateFrom: string = row.startDateFrom?.toLocaleDateString() || null;
    const startDateTo: string = row.startDateTo?.toLocaleDateString() || null;
    const expirationDateFrom: string = row.expirationDateFrom?.toLocaleDateString() || null;
    const expirationDateTo: string = row.expirationDateTo?.toLocaleDateString() || null;
    const endDateFrom: string = row.endDateFrom?.toLocaleDateString() || null;
    const endDateTo: string = row.endDateTo?.toLocaleDateString() || null;

    return (
      <>
        {
          row.rowData.errorMessage
          && (
            <>
              <p className="reportDetailRow redText">
                <strong>Error: </strong>{row.rowData.errorMessage}
              </p>
              <br />
            </>
          )
        }
        <p className="reportDetailRow">
          <strong>Customer Code: </strong>{row.rowData.customerCode ? `${row.rowData.customerCode}` : null}
        </p>
        <p className="reportDetailRow">
          <strong>Jurisdiction: </strong>{row.rowData.jurisdiction ? `${row.rowData.jurisdiction?.name}` : null}
        </p>
        <p className="reportDetailRow">
          <strong>Include All Lower Jurisdictions: </strong>{`${row.rowData.includeLowerJurisdictions}`}
        </p>
        <p className="reportDetailRow">
          <strong>Exemption Type: </strong>{row.rowData.exemption ? `${row.rowData.exemption?.name}` : null}
        </p>
        <p className="reportDetailRow">
          <strong>Certificate Status: </strong>{`${row.rowData.certificateStatus ? row.rowData.certificateStatus : 'All'}`}
        </p>
        <br />
        <p className="reportDetailRow">
          <strong>Certificate Start Date Range: </strong>{startDateFrom && startDateTo ? `${startDateFrom} - ${startDateTo}` : null}
        </p>
        <p className="reportDetailRow">
          {/* eslint-disable-next-line max-len */}
          <strong>Certificate Expiration Date Range: </strong>{expirationDateFrom && expirationDateTo ? `${expirationDateFrom} - ${expirationDateTo}` : null}
        </p>
      </>
    );
  };

  // Check if a Row can be Expanded
  const rowExpandable = (row: GridRowData): boolean => true;

  // Parse each row from the Native Data Type to the Actual Data Type
  const rowParser = (row: GridDataNativeType): GridRowData => GridRowData.fromData(row);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderAction = (value: any, row: GridRowData, index: number): JSX.Element => {
    if (row.rowData.reportExpired) {
      return (<div className="grayText">Report Expired</div>);
    }

    if (row.rowData.errorMessage) {
      return (<div className="redText">Expand row for detail</div>);
    }

    if (row.rowData.reportUrl) {
      return (
        <ColumnLink
          id="downloadLink"
          linkText="Download"
          linkAddress={row.rowData.reportUrl}
          align="center"
          row={row}
        />
      );
    }

    if (!row.rowData.reportUrl && (row.rowData.status === CertificateReportStatus.Success)) {
      return (<>No Results</>);
    }

    return (<></>);
  };

  // Get the Columns for the Grid
  const getColumns = (): VtxColumnProps<GridRowData>[] => {
    // Get the Default Columns
    const defaultColumns: VtxColumnProps<GridRowData>[] = getDefaultColumns();

    // Column Columns with Dynamic Renderer
    const customColumns = [
      {
        dataIndex: 'action',
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (value: any, row: GridRowData, index: number) => renderAction(value, row, index),
      },
    ];

    // Merge the Custom Columns into the Default Columns
    return mergeDefaultAndCustomColumns(defaultColumns, customColumns);
  };

  // The API Call used to retrieve data for the Grid
  const apiGetRowsServerModel = (
    startRow: number,
    endRow: number,
    filterModel: FilterModelApi,
    sortModel: SortModelApi,
  ): Promise<GridDataSummaryCollection> => {
    const api = new API(gatewayEndPoint);

    return api.report.getCertificateReportsV41(startRow, endRow, filterModel, sortModel);
  };

  const onNewCertificateReport = (): void => {
    Logging.Debug('New Certificate Report');
  };

  React.useEffect(() => {
    // This Code will run once after the initial Render
    Logging.Debug('Certificate Reports: Mount');

    return (): void => {
      // This Code will run once the component unmounts.
    };
  }, []);

  const expanderIcons: ExpanderIcons = {
    collapseIcon: 'down',
    expandIcon: 'right',
  };

  return (
    <>
      <div className="reportsContainer">
        <div className="reportsHeader">
          <div className="reportsHeaderLeft">Certificate Reports</div>
          <div className="reportsHeaderRight">
            {permissionReportsCreate && (
              <a
                href="#/reports/certificate/add"
                title="New Certificate Report"
              >
                <VtxButton
                  id="newButton"
                  icon={<VtxIcon type="plus" />}
                  onClick={onNewCertificateReport}
                  type="primary"
                >
                  New Certificate Report
                </VtxButton>
              </a>
            )}
          </div>
        </div>
        <div className="reportsContentGrid">
          <GenericGrid<GridRowData>
            id="certificateReportGrid"
            dataTypeDescription="Certificate Reports"
            expandedRowRender={expandedRowRender}
            rowExpandable={rowExpandable}
            getDefaultColumns={getColumns}
            rowParser={rowParser}
            apiGetRowsServerModel={apiGetRowsServerModel}
            verticalHeightOffset={338}
            model="server"
            expanderIcons={expanderIcons}
            autoRefreshSeconds={15}
            useCache={false} // Cannot use cache if Auto Refresh
          />
        </div>
      </div>
    </>
  );
};

export default CertificateReports;
